/**
 * Settings > Support Categories (board #262).
 *
 * #234 made the Support Category on a ticket come from configuration rather
 * than a free-text box, and shipped the table, the API and the tenant scoping.
 * What it never shipped is the screen: there was no way to add, rename or
 * retire a category anywhere in the product, so every broker on the platform
 * was stuck with the nine rows migration 0007 seeded.
 *
 * Two rules the layout exists to make visible:
 *
 * 1. **A platform row is not yours.** Rows with `is_platform_wide` are seeded
 *    for every tenant. A broker admin can read them but not edit or retire
 *    them — one tenant renaming "Claims" would rename it in every other
 *    tenant's dropdown. The server enforces that (those rows are outside the
 *    write queryset and 404); this page shows Clone instead of Rename so the
 *    button is not a lie.
 * 2. **Retiring is not deleting.** `SupportTicket.support_category` is
 *    SET_NULL, so deleting a category would blank it on every ticket already
 *    filed under it. Retiring takes it out of the ticket form and leaves the
 *    history intact — which is also why this screen asks for inactive rows and
 *    can bring one back.
 */

import { useMemo, useState } from 'react';
import {
  DocumentDuplicateIcon,
  InformationCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

import { Badge, Button, Card, EmptyState, Input, Modal, ModalFooter } from '@/components/common';
import { PageHeader } from '@/components/layout';
import { useAuthStore } from '@/store';
import type { SupportTicketCategory } from '@/services/communications';
import {
  useCloneTicketCategory,
  useCreateTicketCategory,
  useSetTicketCategoryActive,
  useTicketCategoriesAdmin,
  useUpdateTicketCategory,
} from '@/hooks/useCommunications';

/** Roles that own the platform-wide list rather than a broker's own list. */
const PLATFORM_ROLES = ['super_admin', 'country_admin'];

interface EditorState {
  name: string;
  code: string;
  description: string;
  display_order: string;
}

const BLANK: EditorState = { name: '', code: '', description: '', display_order: '0' };

/** `code` is mirrored into the legacy `SupportTicket.category` char column, so
 *  it is capped at that column's 30 characters and kept to a slug shape. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 30);
}

export function SupportCategoriesPage() {
  const user = useAuthStore((state) => state.user);
  const ownsPlatformList = PLATFORM_ROLES.includes(user?.role ?? '');

  const { data: categories, isLoading } = useTicketCategoriesAdmin();
  const [editing, setEditing] = useState<SupportTicketCategory | null>(null);
  const [isEditorOpen, setEditorOpen] = useState(false);

  const { own, platform } = useMemo(() => {
    const rows = categories ?? [];
    return {
      own: rows.filter((row) => !row.is_platform_wide),
      platform: rows.filter((row) => row.is_platform_wide),
    };
  }, [categories]);

  const openAdd = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Support Categories"
        subtitle="The categories a support ticket can be filed under"
        breadcrumbs={[{ label: 'Settings', href: '/settings' }, { label: 'Support Categories' }]}
        actions={
          <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={openAdd}>
            Add Category
          </Button>
        }
      />

      <Card className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/30">
        <div className="flex gap-3">
          <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-300 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 dark:text-blue-100">
            <p className="font-medium">Retiring a category does not delete it.</p>
            <p className="mt-1">
              It disappears from the ticket form, and every ticket already filed under it keeps its
              category. You can bring a retired category back at any time.
            </p>
          </div>
        </div>
      </Card>

      {isLoading ? (
        <Card className="animate-pulse">
          <div className="h-4 w-48 bg-gray-200 rounded mb-3" />
          <div className="h-3 w-72 bg-gray-100 rounded" />
        </Card>
      ) : (
        <div className="space-y-6">
          <CategorySection
            title={ownsPlatformList ? 'Your categories' : 'Your broker’s categories'}
            description="Added here. You can rename, reorder and retire these."
            rows={own}
            emptyTitle="No categories of your own yet"
            emptyDescription={
              ownsPlatformList
                ? 'Add one, or leave the platform list below as it is.'
                : 'Add one, or copy a platform category below and adapt it.'
            }
            onAdd={openAdd}
            onEdit={(row) => {
              setEditing(row);
              setEditorOpen(true);
            }}
            canEdit
            canClone={false}
          />

          {platform.length > 0 && (
            <CategorySection
              title="Platform categories"
              description={
                ownsPlatformList
                  ? 'Offered to every broker on the platform. Editing one changes it everywhere.'
                  : 'Offered to every broker. You can copy one into your own list to adapt it.'
              }
              rows={platform}
              emptyTitle="No platform categories"
              emptyDescription=""
              onEdit={(row) => {
                setEditing(row);
                setEditorOpen(true);
              }}
              canEdit={ownsPlatformList}
              canClone={!ownsPlatformList}
            />
          )}
        </div>
      )}

      <CategoryEditor
        isOpen={isEditorOpen}
        category={editing}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

function CategorySection({
  title,
  description,
  rows,
  emptyTitle,
  emptyDescription,
  onAdd,
  onEdit,
  canEdit,
  canClone,
}: {
  title: string;
  description: string;
  rows: SupportTicketCategory[];
  emptyTitle: string;
  emptyDescription: string;
  onAdd?: () => void;
  onEdit: (row: SupportTicketCategory) => void;
  canEdit: boolean;
  canClone: boolean;
}) {
  return (
    <Card padding="none">
      <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
      </div>

      {rows.length === 0 ? (
        <div className="p-5">
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            action={
              onAdd ? (
                <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={onAdd}>
                  Add Category
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {rows.map((row) => (
            <CategoryRow
              key={row.id}
              category={row}
              onEdit={() => onEdit(row)}
              canEdit={canEdit}
              canClone={canClone}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function CategoryRow({
  category,
  onEdit,
  canEdit,
  canClone,
}: {
  category: SupportTicketCategory;
  onEdit: () => void;
  canEdit: boolean;
  canClone: boolean;
}) {
  const setActive = useSetTicketCategoryActive();
  const clone = useCloneTicketCategory();

  return (
    <li className="px-5 py-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{category.name}</span>
          <Badge variant="default" size="sm">
            {category.code}
          </Badge>
          {category.is_platform_wide ? (
            <Badge variant="info" size="sm">
              Platform
            </Badge>
          ) : (
            <Badge variant="success" size="sm">
              Yours
            </Badge>
          )}
          {!category.is_active && (
            <Badge variant="warning" size="sm">
              Retired
            </Badge>
          )}
        </div>
        {category.description && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{category.description}</p>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {canClone && (
          <Button
            variant="outline"
            size="sm"
            leftIcon={<DocumentDuplicateIcon className="w-4 h-4" />}
            isLoading={clone.isPending}
            onClick={() => clone.mutate(category.id)}
          >
            Copy to my list
          </Button>
        )}
        {canEdit && (
          <>
            <Button variant="outline" size="sm" onClick={onEdit}>
              Rename
            </Button>
            <Button
              variant="outline"
              size="sm"
              isLoading={setActive.isPending}
              onClick={() => setActive.mutate({ id: category.id, isActive: !category.is_active })}
            >
              {category.is_active ? 'Retire' : 'Restore'}
            </Button>
          </>
        )}
      </div>
    </li>
  );
}

function CategoryEditor({
  isOpen,
  category,
  onClose,
}: {
  isOpen: boolean;
  category: SupportTicketCategory | null;
  onClose: () => void;
}) {
  const create = useCreateTicketCategory();
  const update = useUpdateTicketCategory();

  const [form, setForm] = useState<EditorState>(BLANK);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset the fields whenever the modal is pointed at a different row (or at
  // "add"), without an effect that would also clobber what is being typed.
  const key = category?.id ?? '__new__';
  if (isOpen && loadedFor !== key) {
    setLoadedFor(key);
    setError(null);
    setForm(
      category
        ? {
            name: category.name,
            code: category.code,
            description: category.description ?? '',
            display_order: String(category.display_order ?? 0),
          }
        : BLANK,
    );
  }
  if (!isOpen && loadedFor !== null) {
    setLoadedFor(null);
  }

  const submit = () => {
    const name = form.name.trim();
    if (!name) {
      setError('Give the category a name.');
      return;
    }
    // On add, the code is derived from the name so an admin never has to think
    // about it; on edit the code is left alone, because it is mirrored onto
    // every ticket already filed and changing it would orphan them.
    const code = category ? category.code : slugify(name);
    if (!code) {
      setError('That name has no letters or numbers in it, so no code can be made from it.');
      return;
    }

    const payload = {
      name,
      code,
      description: form.description.trim(),
      display_order: Number(form.display_order) || 0,
    };

    if (category) {
      update.mutate({ id: category.id, data: payload }, { onSuccess: onClose });
    } else {
      create.mutate(payload, { onSuccess: onClose });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={category ? 'Rename category' : 'Add support category'}
    >
      <div className="space-y-4">
        {/* Explicit ids: `Input` derives `htmlFor` from `id`/`name`, and without
            one the label is not associated with the field for a screen reader
            (or for a test that looks the field up by its label). */}
        <Input
          id="support-category-name"
          label="Name"
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <Input
          id="support-category-description"
          label="Description"
          value={form.description}
          onChange={(event) => setForm({ ...form, description: event.target.value })}
        />
        <Input
          id="support-category-display-order"
          label="Display order"
          type="number"
          value={form.display_order}
          onChange={(event) => setForm({ ...form, display_order: event.target.value })}
        />
        {category && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Code <span className="font-mono">{category.code}</span> cannot change — it is stored on
            every ticket already filed under this category.
          </p>
        )}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>

      <ModalFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={submit} isLoading={create.isPending || update.isPending}>
          {category ? 'Save' : 'Add Category'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
